import { useTranslation } from 'react-i18next';

const PAGE_WINDOW_DELTA = 2;

type RawPaginationItem = number | '...';

export type PaginationEllipsisItem = {
  key: string;
  type: 'ellipsis';
};

export type PaginationPageItem = {
  ariaLabel: string;
  isActive: boolean;
  key: string;
  onClick: () => void;
  page: number;
  type: 'page';
};

export type PaginationListItem = PaginationEllipsisItem | PaginationPageItem;

type UsePaginationParams = {
  ariaLabel?: string;
  currentPage: number;
  onPageChange: (page: number) => void;
  totalPages: number;
};

export type UsePaginationReturn = {
  ariaNextLabel: string;
  ariaPrevLabel: string;
  ariaStatusLabel: string;
  canNextPage: boolean;
  canPreviousPage: boolean;
  items: PaginationListItem[];
  onNextPage: () => void;
  onPreviousPage: () => void;
  resolvedAriaLabel: string;
};

function getPaginationRange(current: number, total: number): RawPaginationItem[] {
  const range: number[] = [];
  const rangeWithDots: RawPaginationItem[] = [];
  let previous: number | undefined;

  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - PAGE_WINDOW_DELTA && i <= current + PAGE_WINDOW_DELTA)) {
      range.push(i);
    }
  }

  for (const i of range) {
    if (previous !== undefined) {
      if (i - previous === 2) {
        rangeWithDots.push(previous + 1);
      } else if (i - previous !== 1) {
        rangeWithDots.push('...');
      }
    }
    rangeWithDots.push(i);
    previous = i;
  }

  return rangeWithDots;
}

function usePagination({ ariaLabel, currentPage, totalPages, onPageChange }: UsePaginationParams): UsePaginationReturn {
  const { t } = useTranslation();

  const canPreviousPage = currentPage > 1;
  const canNextPage = currentPage < totalPages;
  const resolvedAriaLabel = ariaLabel ?? t('common:PaginationNavLabel');
  const ariaPrevLabel = t('common:PaginationPrevLabel', { page: Math.max(1, currentPage - 1) });
  const ariaNextLabel = t('common:PaginationNextLabel', { page: Math.min(totalPages, currentPage + 1) });
  const ariaStatusLabel = t('common:PaginationStatusLabel', { page: currentPage, total: totalPages });

  function getPageAriaLabel(page: number): string {
    return page === currentPage
      ? t('common:PaginationCurrentPageLabel', { page })
      : t('common:PaginationPageLabel', { page });
  }

  const rawItems = getPaginationRange(currentPage, totalPages);

  const items: PaginationListItem[] = rawItems.map((item, idx) => {
    if (item === '...') {
      return { key: `ellipsis-${idx}`, type: 'ellipsis' };
    }

    const page = item;

    function onClick(): void {
      onPageChange(page);
    }

    return {
      ariaLabel: getPageAriaLabel(page),
      isActive: page === currentPage,
      key: `page-${page}`,
      onClick,
      page,
      type: 'page',
    };
  });

  function onPreviousPage(): void {
    if (canPreviousPage) onPageChange(currentPage - 1);
  }

  function onNextPage(): void {
    if (canNextPage) onPageChange(currentPage + 1);
  }

  return {
    ariaNextLabel,
    ariaPrevLabel,
    ariaStatusLabel,
    canNextPage,
    canPreviousPage,
    items,
    onNextPage,
    onPreviousPage,
    resolvedAriaLabel,
  };
}

export default usePagination;
